import { type Query, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { apiClient } from '../../apiClient'

export type MySuspension = {
  suspensionId: string
  origin: 'manual' | 'automated'
  message?: string | null
  startedAt: string
  programInstanceId: string
  liveProgramId: string
  recyclerProgramId: string | null
  program: {
    name: string
    msDisplayName: string | null
    icon: string | null
    color: string | null
    group: { id: string; name: string; msDisplayName: string | null } | null
  }
  workforceType: 'full_time' | 'part_time'
  shiftConsequences: boolean
  cancelLeadMinutes: number
  nextShift: { startsAt: string; cancellationDeadline: string } | null
  requalification: {
    moduleId: string
    attemptsUsed: number
    maxAttempts: number | null
    status: 'not_started' | 'in_progress' | 'passed'
    exhausted: boolean
    triggeringCategories: string[]
  } | null
}

export type SuspensionNotice = Pick<
  MySuspension,
  'suspensionId' | 'liveProgramId' | 'recyclerProgramId' | 'origin'
> & {
  requalificationModuleId: string | null
}

export const MSMySuspensionsKey = 'mission-specialist-my-suspensions'

const request = async (): Promise<MySuspension[]> => {
  const { data } = await apiClient.get<MySuspension[]>(`/int/v3/suspensions/mine`)
  return data
}

export function indexSuspensionsByProgramInstance(list: MySuspension[]) {
  const byProgramInstance = new Map<string, MySuspension>()
  for (const suspension of list) {
    if (!byProgramInstance.has(suspension.programInstanceId)) {
      byProgramInstance.set(suspension.programInstanceId, suspension)
    }
  }
  return byProgramInstance
}

type UseMySuspensionsOptions = {
  refetchInterval?: number | false | ((query: Query<MySuspension[]>) => number | false)
}

export function useMySuspensions(options?: UseMySuspensionsOptions) {
  return useQuery<MySuspension[]>({
    queryKey: [MSMySuspensionsKey],
    queryFn: request,
    refetchOnWindowFocus: true,
    refetchInterval: options?.refetchInterval,
  })
}

export function useMySuspensionsByProgramInstance() {
  return useQuery<MySuspension[], Error, Map<string, MySuspension>>({
    queryKey: [MSMySuspensionsKey],
    queryFn: request,
    refetchOnWindowFocus: true,
    select: indexSuspensionsByProgramInstance,
  })
}

export function useFetchMySuspensions() {
  const queryClient = useQueryClient()
  return useCallback(async () => {
    await queryClient.cancelQueries({ queryKey: [MSMySuspensionsKey] })
    return queryClient.fetchQuery<MySuspension[]>({
      queryKey: [MSMySuspensionsKey],
      queryFn: request,
      staleTime: 0,
      retry: 2,
    })
  }, [queryClient])
}

export function useInvalidateMySuspensions() {
  const queryClient = useQueryClient()
  return useCallback(() => queryClient.invalidateQueries({ queryKey: [MSMySuspensionsKey] }), [queryClient])
}
